import { Collapse, Divider, Form, Popover, Radio, Switch } from 'antd';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';
import ButtonIconOutlineMedium from '../../../../components/common/ButtonIconOutline/ButtonIconOutlineMedium';
import ButtonPrimaryMedium from '../../../../components/common/ButtonPrimary/ButtonPrimaryMedium';
import ButtonPrimarySmall from '../../../../components/common/ButtonPrimary/ButtonPrimarySmall';
import ButtonSecondarySmall from '../../../../components/common/ButtonSecondary/ButtonSecondarySmall';
import InputComponent from '../../../../components/common/InputComponent/InputComponent';
import openModalFitHeight from '../../../../components/common/Modal/openModalConfirmFitHeight';
import {
  closeNotification,
  openNotification,
} from '../../../../components/common/Notification/openNotification';
import SelectComponent from '../../../../components/common/SelectComponent/SelectComponent';
import Slider from '../../../../components/common/Slider/Slider';
import TabComponent from '../../../../components/common/TabComponent/TabComponent';
import Tooltip from '../../../../components/common/Tooltip/Tooltip';
import RightMenuDesign from '../../../../components/house/RightMenuDesign/RightMenuDesign';
import { Icon } from '../../../../components/icon/icon';
import {
  BACK_THICKNESS_OPTIONS,
  InsideType,
  ModuleType,
} from '../../../../constants/enum';
import i18n from '../../../../languages/i18n';
import { roundTo } from '../../../../utils/geometry/epsilon';
import {
  getCeilingModuleList,
  getUpperModuleList,
} from '../../../../utils/modules/ModuleUtils';
import {
  evaluateExpression,
  getInputType,
} from '../../../../utils/string/inputExpression';
import { DesignerPageContext } from '../../DesignerPage';
import './RightCabinetDesignMenu.scss';

const { Panel } = Collapse;

const RightCabinetDesignMenu = ({ open, form, className }) => {
  const { t } = useTranslation();
  return (
    <RightMenuDesign
      open={open}
      classNames={{
        root: 'top-16 ' + className,
        content: '!p-0 gap-0',
      }}
      header={
        <span className="text-title-large text-nowrap">{t('Structure')}</span>
      }
      content={open ? <RenderContent form={form} /> : null}
    />
  );
};

export default RightCabinetDesignMenu;

const RenderContent = ({ form }) => {
  const {
    mdSelectedModelData,
    currentCabinet,
    mdCustomData,
    setMdSelectedModelData,
    sceneRef,
    selectedModel,
    sceneManager,
    insideLoaderRef,
    selectedModels,
    setTick,
  } = useContext(DesignerPageContext);
  const { t } = useTranslation();
  const [tab, setTab] = useState('1');
  const scaleTimerRef = useRef(null);
  const [currentModal, setCurrentModal] = useState();
  const [activeEditWidth, setActiveEditWidth] = useState(false);
  // const [allowFit, setAllowFit] = useState(false);
  const scaleJob = useRef(null);
  const [formLayout, setFormLayout] = useState([]);

  const watchCurve = Form.useWatch(['curve'], form);
  const watchFitCeiling = Form.useWatch(['fitCeiling'], form);

  const validatorSize = ({ value, min, max }) => {
    const type = getInputType(value);
    if (type === 'text') {
      return Promise.reject(new Error(t('Invalid size')));
    }
    if (['number', 'invalid'].includes(type)) {
      const num = (parseFloat(value) || 0) / 1000;
      if (min !== undefined && max !== undefined) {
        if (num > max || num < min) {
          return Promise.reject(
            new Error(`${min * 1000}mm ${t('to')} ${max * 1000}mm`)
          );
        }
      } else {
        if (num <= 0) {
          return Promise.reject(t('Invalid size'));
        }
      }
    }
    if (type === 'expression') {
      const num = evaluateExpression(value) / 1000;
      if (min !== undefined && max !== undefined) {
        if (num > max || num < min) {
          return Promise.reject(
            new Error(`${min * 1000}mm ${t('to')} ${max * 1000}mm`)
          );
        }
      } else {
        if (num <= 0) {
          return Promise.reject(t('Invalid size'));
        }
      }
    }
    return Promise.resolve();
  };

  const validatorQty = ({ value, min, max }) => {
    const type = getInputType(value);
    if (type === 'text') {
      return Promise.reject(new Error(t('Invalid quantity')));
    }
    if (['number', 'invalid'].includes(type)) {
      const num = parseFloat(value) || 0;
      if (min !== undefined && max !== undefined) {
        if (num > max || num < min) {
          return Promise.reject(
            new Error(`${min} ${t('piece')} ${t('to')} ${max} ${t('piece')} `)
          );
        }
      } else {
        if (num <= 0) {
          return Promise.reject(t('Invalid quantity'));
        }
      }
    }
    if (type === 'expression') {
      const num = evaluateExpression(value) / 1000;
      if (min !== undefined && max !== undefined) {
        if (num > max || num < min) {
          return Promise.reject(
            new Error(`${min} ${t('piece')} ${t('to')} ${max} ${t('piece')} `)
          );
        }
      } else {
        if (num <= 0) {
          return Promise.reject(t('Invalid quantity'));
        }
      }
    }
    return Promise.resolve();
  };

  useEffect(() => {
    if (open) {
      const module = sceneManager.cabinetManager.getModuleByGltfUuid(
        selectedModel?.uuid
      );
      let check = false;
      module?.listInside.forEach(inside => {
        if (
          inside.type === InsideType.ACCESSORY_DISH_LOWER ||
          inside.type === InsideType.ACCESSORY_DISH_UPPER ||
          inside.type === InsideType.ACCESSORY_HOOD
        ) {
          check = true;
        }
      });
      setActiveEditWidth(check);

      //Vị trí bo cong
      let posCurve = undefined;
      if (
        mdSelectedModelData.mdOptions.bct &&
        mdSelectedModelData.mdOptions.bcp
      ) {
        posCurve = 'both';
      } else if (mdSelectedModelData.mdOptions.bct) {
        posCurve = 'bct';
      } else if (mdSelectedModelData.mdOptions.bcp) {
        posCurve = 'bcp';
      }

      //Vị trí ốp vách
      let posBacksplash = undefined;
      if (
        mdSelectedModelData.mdOptions.opVachTrai &&
        mdSelectedModelData.mdOptions.opVachPhai
      ) {
        posBacksplash = 'both';
      } else if (mdSelectedModelData.mdOptions.opVachSau) {
        posBacksplash = 'opVachSau';
      } else if (mdSelectedModelData.mdOptions.opVachTrai) {
        posBacksplash = 'opVachTrai';
      } else if (mdSelectedModelData.mdOptions.opVachPhai) {
        posBacksplash = 'opVachPhai';
      }

      //Vị trí ốp vách bìa
      let posEdgeBacksplash = undefined;
      if (
        mdSelectedModelData.mdOptions.opVachBiaTrai &&
        mdSelectedModelData.mdOptions.opVachBiaPhai
      ) {
        posEdgeBacksplash = 'both';
      } else if (mdSelectedModelData.mdOptions.opVachBiaTrai) {
        posEdgeBacksplash = 'opVachBiaTrai';
      } else if (mdSelectedModelData.mdOptions.opVachBiaPhai) {
        posEdgeBacksplash = 'opVachBiaPhai';
      }

      //Bo cong ốp vách bìa
      let posCurveEdgeBacksplash = undefined;
      if (mdSelectedModelData.mdOptions.opVachBiaBoCong) {
        posCurveEdgeBacksplash = 'both';
      }

      form.setFieldsValue({
        width: roundTo(mdSelectedModelData.mdDimensions.width * 1000),
        height: roundTo(mdSelectedModelData.mdDimensions.height * 1000),
        depth: roundTo(mdSelectedModelData.mdDimensions.depth * 1000),
        DDTHau: roundTo(mdSelectedModelData.mdOptions.DDTHau * 1000),
        dayCot: roundTo(mdSelectedModelData.mdOptions.dayCot * 1000),
        rongCot: roundTo(mdSelectedModelData.mdOptions.rongCot * 1000),
        KCCot: roundTo(mdSelectedModelData.mdOptions.KCCot * 1000),
        caoDa: roundTo(mdSelectedModelData.mdOptions.caoDa * 1000),
        rongDa: roundTo(mdSelectedModelData.mdOptions.rongDa * 1000),
        dayDa: roundTo(mdSelectedModelData.mdOptions.dayDa * 1000),
        caoChan: roundTo(mdSelectedModelData.mdOptions.caoChan * 1000),
        rongK1: roundTo(mdSelectedModelData.mdOptions.rongK1 * 1000),
        rongK2: roundTo(mdSelectedModelData.mdOptions.rongK2 * 1000),
        caoKhungHoc: roundTo(mdSelectedModelData.mdOptions.caoKhungHoc * 1000),
        caoVachLam: roundTo(mdSelectedModelData.mdOptions.caoVachLam * 1000),
        rounded1: roundTo(mdSelectedModelData.mdOptions.rounded1 * 1000),
        rounded2: roundTo(mdSelectedModelData.mdOptions.rounded2 * 1000),
        rounded3: roundTo(mdSelectedModelData.mdOptions.rounded3 * 1000),
        rounded4: roundTo(mdSelectedModelData.mdOptions.rounded4 * 1000),
        extend1: roundTo(mdSelectedModelData.mdOptions.extend1 * 1000),
        extend2: roundTo(mdSelectedModelData.mdOptions.extend2 * 1000),
        extend3: roundTo(mdSelectedModelData.mdOptions.extend3 * 1000),
        extend4: roundTo(mdSelectedModelData.mdOptions.extend4 * 1000),
        rBo: roundTo(mdSelectedModelData.mdOptions.rBo * 1000),
        rBoOpVachBia: roundTo(
          mdSelectedModelData.mdOptions.rBoOpVachBia * 1000
        ),
        coTamChan: mdSelectedModelData.mdOptions.coTamChan,
        fitCeiling: module?.fitCeiling,

        //Hau ngang
        hauNgang: mdSelectedModelData.mdOptions.hauNgang,

        //Hoc keo giuong
        qtyDrawer: mdSelectedModelData.mdOptions.qtyDrawer,
        wDrawer: roundTo(mdSelectedModelData.mdOptions.wDrawer * 1000),
        hDrawer: roundTo(mdSelectedModelData.mdOptions.hDrawer * 1000),
        dDrawer: roundTo(mdSelectedModelData.mdOptions.dDrawer * 1000),

        //Canh lat giuong
        qtyFlipDoor: mdSelectedModelData.mdOptions.qtyFlipDoor,
        wFlipDoor: roundTo(mdSelectedModelData.mdOptions.wFlipDoor * 1000),
        hFlipDoor: roundTo(mdSelectedModelData.mdOptions.hFlipDoor * 1000),
        dFlipDoor: roundTo(mdSelectedModelData.mdOptions.dFlipDoor * 1000),

        //Bo cong
        bct: mdSelectedModelData.mdOptions.bct,
        bcp: mdSelectedModelData.mdOptions.bcp,
        posCurve: posCurve,
        curve: posCurve,

        //Op vach bia
        opVachBiaTrai: mdSelectedModelData.mdOptions.opVachBiaTrai,
        opVachBiaPhai: mdSelectedModelData.mdOptions.opVachBiaPhai,
        posEdgeBacksplash: posEdgeBacksplash,
        edgeBacksplash: posEdgeBacksplash,
        curveEdgeBacksplash: posCurveEdgeBacksplash,

        //Op vach bep
        opVachSau: mdSelectedModelData.mdOptions.opVachSau,
        opVachTrai: mdSelectedModelData.mdOptions.opVachTrai,
        opVachPhai: mdSelectedModelData.mdOptions.opVachPhai,
        posBacksplash: posBacksplash,
        hasBacksplash: posBacksplash,
      });
    }
  }, [mdSelectedModelData, open, activeEditWidth, tab]);

  useEffect(() => {
    let formLayout = [];
    if (
      [ModuleType.BACKSPLASH, ModuleType.COUNTERTOP].some(
        item => mdSelectedModelData.type === item
      )
    ) {
      formLayout = [formForStone.stone];
    } else {
      if (tab === '2') {
        formLayout = [...formColumn.column];
      } else if (tab === '3') {
        formLayout = [...formBeam.beam];
      } else {
        formLayout = [
          ...formForCurrentCabinet.cabinet,
          ...(mdSelectedModelData?.mdOptions?.DDTHau !== undefined &&
          !mdSelectedModelData?.moduleType?.includes('bed')
            ? formForCurrentCabinet.backPlank
            : []),
          ...(mdSelectedModelData?.mdOptions?.caoChan !== undefined &&
          mdSelectedModelData?.moduleType?.includes('lower') &&
          // !['shoe_lower', 'totl_lower'].some(item =>
          !['totl_lower'].some(item =>
            mdSelectedModelData?.moduleType?.includes(item)
          ) &&
          !mdSelectedModelData?.mdOptions?.caoChanRoi
            ? formForCurrentCabinet.cabinetLegs
            : []),
          ...(mdSelectedModelData?.mdOptions?.caoKhungHoc !== undefined
            ? formForCurrentCabinet.drawer
            : []),
          ...(mdSelectedModelData?.mdOptions?.caoVachLam !== undefined &&
          ['shoe_lower_bench'].some(item =>
            mdSelectedModelData?.moduleType?.includes(item)
          )
            ? formForCurrentCabinet.vachLam
            : []),
          ...(mdSelectedModelData?.mdOptions?.rBo !== undefined &&
          [
            'shoe_lower_bench',
            'curved_block',
            'tv_lower',
            'tv_upper',
            'wardrobe',
          ].some(item => mdSelectedModelData?.moduleType?.includes(item))
            ? formForCurrentCabinet.curve
            : []),
          // ...(mdSelectedModelData?.mdOptions?.coTamChan !== undefined &&
          // mdSelectedModelData?.mdOptions?.caoChan !== undefined &&
          // ['shoe_lower', 'wardrobe'].some(item =>
          //   mdSelectedModelData?.moduleType?.includes(item)
          // )
          //   ? formForCurrentCabinet.curveCabinetLegs
          //   : []),
          ...((mdSelectedModelData?.mdOptions?.opVachTrai !== undefined ||
            mdSelectedModelData?.mdOptions?.opVachSau !== undefined) &&
          ['kitchen', 'lower'].every(item =>
            mdSelectedModelData?.moduleType?.includes(item)
          )
            ? formForCurrentCabinet.hasBacksplash
            : []),
          ...(mdSelectedModelData?.mdOptions?.opVachBiaTrai !== undefined &&
          ['kitchen', 'lower'].every(item =>
            mdSelectedModelData?.moduleType?.includes(item)
          )
            ? formForCurrentCabinet.edgeBacksplash
            : []),
          ...(['kitchen', 'island'].every(item =>
            mdSelectedModelData?.moduleType?.includes(item)
          )
            ? formForCurrentCabinet.countertopCurve
            : []),
          ...(mdSelectedModelData?.mdOptions?.wDrawer !== undefined &&
          ['bed', 'lower'].every(item =>
            mdSelectedModelData?.moduleType?.includes(item)
          )
            ? formForCurrentCabinet.bedDrawer
            : []),
          ...(mdSelectedModelData?.mdOptions?.wFlipDoor !== undefined &&
          ['bed', 'lower'].every(item =>
            mdSelectedModelData?.moduleType?.includes(item)
          )
            ? formForCurrentCabinet.bedFlipDoor
            : []),
        ];
      }
    }

    setFormLayout([...formLayout]);
  }, [tab, mdSelectedModelData, watchFitCeiling]);

  const formForStone = {
    stone: {
      key: 'backsplash',
      label: <span className="text-title-medium">{t('Customize')}</span>,
      children: (
        <div className="flex flex-col gap-16 items-end self-stretch">
          <Form.Item
            label={
              <span className="label-text-name flex gap-4 items-center">
                {t('Length')}
              </span>
            }
            className="self-stretch"
            name={'width'}
            rules={
              [
                // {
                //   validator: (_, value) => {
                //     const { max, min } =
                //       mdSelectedModelData?.mdLimitedDimensions?.width;
                //     return validatorSize({ value, min, max });
                //   },
                // },
              ]
            }
          >
            <InputComponent
              addonAfter={<span className="text-body-medium">mm</span>}
              placeholder={'Nhập'}
              disabled={true}
            />
          </Form.Item>

          <Form.Item
            label={
              <span className="label-text-name flex gap-4 items-center">
                {t('Depth')}
              </span>
            }
            className="self-stretch"
            name={'depth'}
            rules={
              [
                // {
                //   validator: (_, value) => {
                //     const { max, min } =
                //       mdSelectedModelData.mdLimitedDimensions?.depth;
                //     return validatorSize({
                //       value,
                //       min,
                //       max,
                //     });
                //   },
                // },
              ]
            }
          >
            <InputComponent
              addonAfter={<span className="text-body-medium">mm</span>}
              placeholder={'Nhập'}
              disabled={true}
            />
          </Form.Item>
          <div className="flex items-end gap-8 self-stretch">
            <Form.Item
              label={
                <span className="label-text-name flex gap-4 items-center grow">
                  {t('Height')}
                  <span className="support-text-medium">
                    {`${t('From')} 550 mm ${t('to')} 700 mm`}
                  </span>
                </span>
              }
              layout="vertical"
              className="self-stretch grow"
              name={'height'}
              rules={[
                {
                  validator: (_, value) => {
                    return validatorSize({
                      value,
                      min: 0.55,
                      max: 0.7,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={'Nhập'}
                disabled={mdSelectedModelData.type !== ModuleType.BACKSPLASH}
              />
            </Form.Item>
          </div>
        </div>
      ),
    },
  };

  const formForCurrentCabinet = {
    cabinet: [
      {
        key: 'cabinet',
        label: <span className="text-title-medium">{t('Size')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            {mdSelectedModelData.moduleType?.includes('corner') ? (
              <>
                <div className="flex flex-col gap-6 self-stretch items-start">
                  <Form.Item
                    layout="vertical"
                    label={
                      <div className="flex items-center justify-between self-stretch">
                        <span className="label-text-name flex gap-4 items-center">
                          {t('Length')}
                          <span className="support-text-medium">
                            ({t('Left block')} {t('From')}{' '}
                            {(mdSelectedModelData?.mdOptions?.limitRongK1
                              ?.min ?? 0) * 1000}{' '}
                            {t('to')}{' '}
                            {(mdSelectedModelData?.mdOptions?.limitRongK1
                              ?.max ?? 0) * 1000}{' '}
                            mm)
                          </span>
                        </span>
                      </div>
                    }
                    labelCol={{ className: 'label-col-full' }}
                    tooltip={{
                      title: t('Cannot apply to all'),
                      className: 'w-16 h-16 flex absolute right-0',
                      getTooltipContainer: node => document.body,
                      icon: (
                        <span className="w-16 h-16 flex items-center">
                          <Icon.ICDeselect />
                        </span>
                      ),
                    }}
                    rules={[
                      {
                        validator: (_, value) => {
                          const { max, min } =
                            mdSelectedModelData.mdOptions?.limitRongK1;
                          return validatorSize({ value, min, max });
                        },
                      },
                    ]}
                    className="self-stretch"
                    name={'rongK1'}
                  >
                    <InputComponent
                      addonBefore={
                        <span className="text-body-medium">
                          {t('Left block')}
                        </span>
                      }
                      addonAfter={<span className="text-body-medium">mm</span>}
                      placeholder={t('Enter')}
                    />
                  </Form.Item>
                  <Form.Item
                    layout="vertical"
                    label={
                      <div className="flex items-center justify-between self-stretch">
                        <span className="support-text-medium">
                          ({t('Right block')} {t('From')}{' '}
                          {(mdSelectedModelData?.mdOptions?.limitRongK2?.min ??
                            0) * 1000}{' '}
                          {t('to')}{' '}
                          {(mdSelectedModelData?.mdOptions?.limitRongK2?.max ??
                            0) * 1000}{' '}
                          mm)
                        </span>
                      </div>
                    }
                    labelCol={{ className: 'label-col-full' }}
                    tooltip={{
                      title: t('Cannot apply to all'),
                      className: 'w-16 h-16 flex absolute right-0',
                      getTooltipContainer: node => document.body,
                      icon: (
                        <span className="w-16 h-16 flex items-center">
                          <Icon.ICDeselect />
                        </span>
                      ),
                    }}
                    className="self-stretch"
                    rules={[
                      {
                        validator: (_, value) => {
                          const { max, min } =
                            mdSelectedModelData.mdOptions?.limitRongK2;
                          return validatorSize({ value, min, max });
                        },
                      },
                    ]}
                    name={'rongK2'}
                  >
                    <InputComponent
                      addonBefore={
                        <span className="text-body-medium">
                          {t('Right block')}
                        </span>
                      }
                      addonAfter={<span className="text-body-medium">mm</span>}
                      placeholder={t('Enter')}
                    />
                  </Form.Item>
                </div>
              </>
            ) : (
              <>
                <Form.Item
                  layout="vertical"
                  label={
                    <div className="flex items-center justify-between self-stretch">
                      <span className="label-text-name flex gap-4 items-center">
                        {mdSelectedModelData.moduleType?.includes('bed')
                          ? t('Width')
                          : t('Length')}
                        <span className="support-text-medium">
                          ({t('From')}{' '}
                          {(mdSelectedModelData?.mdLimitedDimensions?.width
                            ?.min ?? 0) * 1000}{' '}
                          {t('to')}{' '}
                          {(mdSelectedModelData?.mdLimitedDimensions?.width
                            ?.max ?? 0) * 1000}{' '}
                          mm)
                        </span>
                      </span>
                    </div>
                  }
                  className="self-stretch"
                  labelCol={{ className: 'label-col-full' }}
                  tooltip={
                    !mdSelectedModelData.moduleType?.includes('bed')
                      ? {
                          title: t('Cannot apply to all'),
                          className: 'w-16 h-16 flex absolute right-0',
                          getTooltipContainer: node => document.body,
                          icon: (
                            <span className="w-16 h-16 flex items-center">
                              <Icon.ICDeselect />
                            </span>
                          ),
                        }
                      : null
                  }
                  name={'width'}
                  rules={[
                    {
                      validator: (_, value) => {
                        const { max, min } =
                          mdSelectedModelData?.mdLimitedDimensions?.width;
                        return validatorSize({
                          value,
                          min,
                          max,
                        });
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonAfter={<span className="text-body-medium">mm</span>}
                    placeholder={t('Enter')}
                    disabled={activeEditWidth}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item
              layout="vertical"
              className="self-stretch"
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {mdSelectedModelData.moduleType?.includes('bed')
                    ? t('Length')
                    : t('Depth')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdSelectedModelData.mdLimitedDimensions?.depth?.min ??
                      0) * 1000}{' '}
                    {t('to')}{' '}
                    {(mdSelectedModelData.mdLimitedDimensions?.depth?.max ??
                      0) * 1000}{' '}
                    mm)
                  </span>
                </span>
              }
              name={'depth'}
              labelCol={{ className: 'label-col-full' }}
              tooltip={
                mdSelectedModelData.moduleType?.includes('bed')
                  ? {
                      title: t('Cannot apply to all'),
                      className: 'w-16 h-16 flex absolute right-0',
                      getTooltipContainer: node => document.body,
                      icon: (
                        <span className="w-16 h-16 flex items-center">
                          <Icon.ICDeselect />
                        </span>
                      ),
                    }
                  : null
              }
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdLimitedDimensions?.depth;
                    return validatorSize({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>

            <div className="flex items-end gap-8 self-stretch">
              <Form.Item
                layout="vertical"
                label={
                  <span className="label-text-name flex gap-4 items-center grow">
                    {t('Height')}
                    <span className="support-text-medium">
                      ({t('From')}{' '}
                      {(mdSelectedModelData.mdLimitedDimensions?.height?.min ??
                        0) * 1000}{' '}
                      {t('to')}{' '}
                      {(mdSelectedModelData.mdLimitedDimensions?.height?.max ??
                        0) * 1000}{' '}
                      mm)
                    </span>
                  </span>
                }
                className="self-stretch grow"
                name={'height'}
                rules={[
                  {
                    validator: (_, value) => {
                      const { max, min } =
                        mdSelectedModelData.mdLimitedDimensions?.height;
                      return validatorSize({
                        value,
                        min,
                        max,
                      });
                    },
                  },
                ]}
              >
                <InputComponent
                  addonAfter={<span className="text-body-medium">mm</span>}
                  placeholder={t('Enter')}
                  size={'default'}
                />
              </Form.Item>
              {['upper', 'ceiling'].some(item =>
                mdSelectedModelData.moduleType?.includes(item)
              ) &&
                !['shoe_upper_curve'].some(item =>
                  mdSelectedModelData.moduleType?.includes(item)
                ) && (
                  <Form.Item name={'fitCeiling'}>
                    <Tooltip title={t('Adjust ceiling height')}>
                      <ButtonIconOutlineMedium
                        type="button"
                        className={watchFitCeiling ? 'active' : ''}
                        icon={<Icon.ICHeight />}
                        onClick={() => {
                          handleFitCeiling(!watchFitCeiling);
                          form.setFieldsValue({
                            fitCeiling: !watchFitCeiling,
                          });
                          // setAllowFit(prev => {
                          //   return !prev;
                          // });
                        }}
                      />
                    </Tooltip>
                  </Form.Item>
                )}
            </div>

            {mdSelectedModelData.moduleType !== 'totl_lower' &&
            mdSelectedModelData.moduleType !== 'kitchen_lower_wash' ? (
              <Form.Item
                className="flex items-center gap-8 self-stretch"
                style={{ marginTop: '-10px' }}
              >
                <div className="flex items-center gap-8 self-stretch">
                  <Divider
                    type="vertical"
                    style={{ margin: 0, height: '24px' }}
                  />
                  <ButtonSecondarySmall
                    posIcon={<Icon.ICSelectAll />}
                    title={t('Apply to all')}
                    type={'button'}
                    // onClick={async () => {
                    //   await form
                    //     .validateFields(['height', 'depth'])
                    //     .then(values => {
                    //       if (
                    //         getInputType(values.height) === 'number' &&
                    //         getInputType(values.depth) === 'number'
                    //       ) {
                    //         const height = values.height / 1000;
                    //         const depth = values.depth / 1000;
                    //         if (watchFitCeiling) {
                    //           applyToAllCabinets(
                    //             { depth },
                    //             undefined,
                    //             watchFitCeiling
                    //           );
                    //         } else applyToAllCabinets({ height, depth });
                    //       }
                    //     })
                    //     .catch(_ => {});
                    // }}

                    onClick={async () => {
                      const secondField = ['bed', 'lower'].every(item =>
                        mdSelectedModelData?.moduleType?.includes(item)
                      )
                        ? 'width'
                        : 'depth';

                      await form
                        .validateFields(['height', secondField])
                        .then(values => {
                          const secondValue = values[secondField]; // width hoặc depth

                          if (
                            getInputType(values.height) === 'number' &&
                            getInputType(secondValue) === 'number'
                          ) {
                            const height = values.height / 1000;
                            const dimension = secondValue / 1000;

                            if (watchFitCeiling) {
                              applyToAllCabinets(
                                { [secondField]: dimension },
                                undefined,
                                watchFitCeiling
                              );
                            } else {
                              applyToAllCabinets({
                                height,
                                [secondField]: dimension,
                              });
                            }
                          }
                        })
                        .catch(_ => {});
                    }}
                  />
                </div>
              </Form.Item>
            ) : (
              <Popover
                getTooltipContainer={() => document.body}
                placement="leftTop"
                trigger={['click']}
                classNames={{
                  body: '!p-0 rounded-[4px]',
                }}
                styles={{
                  body: { maxWidth: '302px', minWidth: '302px' },
                }}
                content={
                  <div className="flex flex-col gap-0 self-stretch items-start justify-start">
                    <span
                      className="text-title-medium pt-16 px-12 pb-12"
                      style={{ fontWeight: 700 }}
                    >
                      {t('Enter equipment dimensions')}
                    </span>
                    <div className="flex-col flex p-12 gap-12 self-stretch">
                      <Form.Item
                        label={
                          <span className="label-text-name flex gap-4 items-center">
                            {t('Length')}
                          </span>
                        }
                        layout="vertical"
                        name={'equipmentWidth'}
                        rules={[
                          {
                            validator: (_, value) => {
                              return validatorSize({
                                value,
                              });
                            },
                          },
                        ]}
                      >
                        <InputComponent
                          addonAfter={
                            <span className="text-body-medium">mm</span>
                          }
                          placeholder={t('Enter')}
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          <span className="label-text-name flex gap-4 items-center">
                            {t('Depth')}
                          </span>
                        }
                        layout="vertical"
                        name={'equipmentDepth'}
                        rules={[
                          {
                            validator: (_, value) => {
                              return validatorSize({
                                value,
                              });
                            },
                          },
                        ]}
                      >
                        <InputComponent
                          addonAfter={
                            <span className="text-body-medium">mm</span>
                          }
                          placeholder={t('Enter')}
                        />
                      </Form.Item>
                      <Form.Item
                        label={
                          <span className="label-text-name flex gap-4 items-center">
                            {t('Height')}
                          </span>
                        }
                        layout="vertical"
                        name={'equipmentHeight'}
                        rules={[
                          {
                            validator: (_, value) => {
                              return validatorSize({
                                value,
                              });
                            },
                          },
                        ]}
                      >
                        <InputComponent
                          addonAfter={
                            <span className="text-body-medium">mm</span>
                          }
                          placeholder={t('Enter')}
                        />
                      </Form.Item>
                    </div>
                    <Divider type="horizontal" className="!p-0 m-0" />
                    <div className="flex pt-8 px-12 pb-12 self-stretch items-end justify-end">
                      <ButtonPrimaryMedium
                        title={t('Confirm')}
                        type={'button'}
                        onClick={() => {
                          form
                            .validateFields([
                              'equipmentWidth',
                              'equipmentHeight',
                              'equipmentDepth',
                            ])
                            .then(async values => {
                              if (
                                mdSelectedModelData.moduleType ===
                                'kitchen_lower_dwash'
                              ) {
                                const width =
                                  (parseFloat(values['equipmentWidth']) + 54) /
                                  1000;
                                const module =
                                  sceneManager.cabinetManager.getModuleByGltfUuid(
                                    selectedModel?.uuid
                                  );
                                insideLoaderRef.current?.scaleWashAndFridge?.(
                                  parseFloat(values['equipmentWidth']) / 1000,
                                  parseFloat(values['equipmentHeight']) / 1000,
                                  parseFloat(values['equipmentDepth']) / 1000,
                                  module
                                );
                                const currentDimensions =
                                  module.formularController.currentDimensions;
                                const newHeight = currentDimensions.height;
                                const newDepth = currentDimensions.depth;
                                await handleScaleModule({
                                  width,
                                  newHeight,
                                  newDepth,
                                });

                                setTimeout(() => {
                                  sceneManager.calculateTotalCost?.({});
                                }, 0);
                              }
                              if (
                                mdSelectedModelData.moduleType === 'totl_lower'
                              ) {
                                const width =
                                  (parseFloat(values['equipmentWidth']) + 134) /
                                  1000;
                                const height =
                                  (parseFloat(values['equipmentHeight']) + 67) /
                                  1000;
                                const module =
                                  sceneManager.cabinetManager.getModuleByGltfUuid(
                                    selectedModel?.uuid
                                  );

                                const currentDimensions =
                                  module.formularController.currentDimensions;
                                const newDepth = currentDimensions.depth;

                                insideLoaderRef.current?.scaleWashAndFridge?.(
                                  parseFloat(values['equipmentWidth']) / 1000,
                                  parseFloat(values['equipmentHeight']) / 1000,
                                  parseFloat(values['equipmentDepth']) / 1000,
                                  module
                                );
                                await handleScaleModule({
                                  width,
                                  height,
                                  newDepth,
                                });

                                setTimeout(() => {
                                  sceneManager.calculateTotalCost?.({});
                                }, 100);
                              }
                            })
                            .catch(_ => {});
                        }}
                      />
                    </div>
                  </div>
                }
              >
                <ButtonSecondarySmall
                  posIcon={<Icon.ICFridge />}
                  title={t('Dimensions based on equipment')}
                  type={'button'}
                />
              </Popover>
            )}
          </div>
        ),
      },
    ],
    backPlank: [
      {
        key: 'backPlank',
        label: <span className="text-title-medium">{t('Back plank')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            {mdSelectedModelData?.mdOptions?.hauNgang !== undefined && (
              <Form.Item
                layout="horizontal"
                className="self-stretch"
                label={t('Horizontal back plank')}
                name={'hauNgang'}
              >
                <Switch className="ml-auto" />
              </Form.Item>
            )}

            {!mdSelectedModelData?.mdOptions?.hauNgang && (
              <Form.Item
                layout="vertical"
                name={'DDTHau'}
                label={t('Back thickness')}
                className="self-stretch"
              >
                <SelectComponent
                  options={BACK_THICKNESS_OPTIONS}
                  placeholder={t('Select back plank type')}
                  style={{ flex: '1 0 0' }}
                  size={'default'}
                />
              </Form.Item>
            )}

            <Form.Item
              layout="vertical"
              className="flex items-center gap-8 self-stretch"
              style={{ marginTop: '-10px' }}
            >
              <div className="flex items-center gap-8 self-stretch">
                <Divider
                  type="vertical"
                  style={{ margin: 0, height: '24px' }}
                />
                <ButtonSecondarySmall
                  posIcon={<Icon.ICSelectAll />}
                  title={t('Apply to all')}
                  type={'button'}
                  onClick={() => {
                    form
                      .validateFields(['DDTHau', 'hauNgang'])
                      .then(values => {
                        if (getInputType(values.DDTHau) !== 'number') return;
                        const DDTHau = values.DDTHau / 1000;
                        const hauNgang = values.hauNgang;

                        applyToAllCabinets(
                          {},
                          {
                            DDTHau,
                            hauNgang,
                          }
                        );

                        setTick(t => t + 1);
                      })
                      .catch(_ => {});
                  }}
                />
              </div>
            </Form.Item>
          </div>
        ),
      },
    ],
    cabinetLegs: [
      {
        key: 'cabinetLegs',
        label: <span className="text-title-medium">{t('Cabinet legs')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            {mdSelectedModelData?.mdOptions?.coTamChan !== undefined && (
              <Form.Item
                layout="horizontal"
                className="self-stretch"
                label={t('Front leg cover panel')}
                name={'coTamChan'}
              >
                <Switch className="ml-auto" />
              </Form.Item>
            )}

            <Form.Item
              layout="vertical"
              name={'caoChan'}
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {t('Height')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitCaoChan?.min ?? 0) *
                      1000}{' '}
                    {t('to')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitCaoChan?.max ?? 0) *
                      1000}{' '}
                    mm)
                  </span>
                </span>
              }
              className="self-stretch"
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdOptions?.limitCaoChan;
                    return validatorSize({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>

            <Form.Item
              layout="vertical"
              className="flex items-center gap-8 self-stretch"
              style={{ marginTop: '-10px' }}
            >
              <div className="flex items-center gap-8 self-stretch">
                <Divider
                  type="vertical"
                  style={{ margin: 0, height: '24px' }}
                />
                <ButtonSecondarySmall
                  posIcon={<Icon.ICSelectAll />}
                  title={t('Apply to all')}
                  type={'button'}
                  onClick={() => {
                    form
                      .validateFields(['caoChan'])
                      .then(values => {
                        if (getInputType(values.caoChan) !== 'number') return;
                        const caoChan = values.caoChan / 1000;

                        applyToAllCabinets(
                          {},
                          {
                            caoChan,
                          }
                        );

                        setTick(t => t + 1);
                      })
                      .catch(_ => {});
                  }}
                />
              </div>
            </Form.Item>
          </div>
        ),
      },
    ],
    vachLam: [
      {
        key: 'vachLam',
        label: <span className="text-title-medium">{t('Slat panel')}</span>,
        children: (
          <div className="flex items-end gap-8 self-stretch">
            <Form.Item
              layout="vertical"
              name={'caoVachLam'}
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {t('Height')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitCaoVachLam?.min ??
                      0) * 1000}{' '}
                    {t('to')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitCaoVachLam?.max ??
                      0) * 1000}{' '}
                    mm)
                  </span>
                </span>
              }
              className="self-stretch"
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdOptions?.limitCaoVachLam;
                    return validatorSize({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
            {['vach_lam', 'vach_guong', 'vach_tron'].some(item =>
              mdSelectedModelData.moduleType?.includes(item)
            ) && (
              <Form.Item name={'fitCeiling'}>
                <Tooltip title={t('Adjust ceiling height')}>
                  <ButtonIconOutlineMedium
                    type="button"
                    className={watchFitCeiling ? 'active' : ''}
                    icon={<Icon.ICHeight />}
                    onClick={() => {
                      handleFitCeiling(!watchFitCeiling);
                      form.setFieldsValue({
                        fitCeiling: !watchFitCeiling,
                      });
                    }}
                  />
                </Tooltip>
              </Form.Item>
            )}
          </div>
        ),
      },
    ],
    drawer: [
      {
        key: 'drawer',
        label: (
          <span className="text-title-medium">{t('Drawer frame height')}</span>
        ),
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item
              layout="vertical"
              name={'caoKhungHoc'}
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {t('Height')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitCaoKhungHoc?.min ??
                      0) * 1000}{' '}
                    {t('to')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitCaoKhungHoc?.max ??
                      0) * 1000}{' '}
                    mm)
                  </span>
                </span>
              }
              className="self-stretch"
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdOptions?.limitCaoKhungHoc;
                    return validatorSize({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
          </div>
        ),
      },
    ],
    curve: [
      {
        key: 'curve',
        label: <span className="text-title-medium">{t('Curved panel')}</span>,
        children: ['curved_block'].some(item =>
          mdSelectedModelData?.moduleType?.includes(item)
        ) ? (
          <Form.Item
            name={'rBo'}
            layout="vertical"
            className="self-stretch"
            label={
              <div className="flex flex-col justify-center items-start gap-[2px]">
                <span className="body-text-module font-semibold">
                  {t('Curved radius')}
                </span>
                {mdSelectedModelData.mdOptions?.limitRBo && (
                  <span className="text-body-small">{`R${(mdSelectedModelData.mdOptions?.limitRBo?.min ?? 0) * 1000} - R${(mdSelectedModelData.mdOptions?.limitRBo?.max ?? 0) * 1000}`}</span>
                )}
              </div>
            }
            rules={[
              {
                validator: (_, value) => {
                  const { max, min } = mdSelectedModelData.mdOptions?.limitRBo;
                  if (max === undefined || min === undefined)
                    return Promise.resolve();
                  return validatorSize({
                    value,
                    min,
                    max,
                  });
                },
              },
            ]}
          >
            <InputComponent addonAfter={'R(mm)'} />
          </Form.Item>
        ) : (
          <div className="flex flex-col gap-[12px] items-start self-stretch">
            <Form.Item
              layout="horizontal"
              className="self-stretch"
              label={t('Curved corner')}
              name={'curve'}
            >
              <Switch className="ml-auto" />
            </Form.Item>
            {(mdSelectedModelData.mdOptions.bct ||
              mdSelectedModelData.mdOptions.bcp) && (
              <>
                <Divider type="horizontal" className="m-0 !p-0" />
                <div className="flex flex-col items-start gap-[12px] self-stretch">
                  <Form.Item
                    layout="vertical"
                    className="self-stretch"
                    label={t('Position')}
                    name={'posCurve'}
                  >
                    <Radio.Group
                      block
                      options={[
                        ...[
                          { label: t('Left'), value: 'bct' },
                          { label: t('Right'), value: 'bcp' },
                          { label: t('Both'), value: 'both' },
                        ].map(({ label, value }) => ({
                          label: (
                            <span className="text-label-medium font-semibold">
                              {label}
                            </span>
                          ),
                          value,
                          className:
                            '[&_.ant-radio-button-label]:!grow [&_.ant-radio-button-label]:!flex-shrink-0 !border-none !rounded-[1000px] py-[7px] px-[16px] flex justify-center items-center bg-[#F1F2F2]',
                        })),
                      ]}
                      className="flex items-center justify-start gap-[8px] text-black/60 "
                      optionType="button"
                      buttonStyle="solid"
                    />
                  </Form.Item>
                  <Form.Item
                    name={'rBo'}
                    layout="vertical"
                    className="self-stretch"
                    label={
                      <div className="flex flex-col justify-center items-start gap-[2px]">
                        <span className="body-text-module font-semibold">
                          {t('Curved radius')}
                        </span>
                        <span className="text-body-small">(R50 - R200)</span>
                      </div>
                    }
                    rules={[
                      {
                        validator: (_, value) => {
                          const { max, min } =
                            mdSelectedModelData.mdOptions?.limitRBo;
                          return validatorSize({
                            value,
                            min,
                            max,
                          });
                        },
                      },
                    ]}
                  >
                    <InputComponent addonAfter={'R(mm)'} />
                  </Form.Item>
                </div>
              </>
            )}
          </div>
        ),
      },
    ],
    curveCabinetLegs: [
      {
        key: 'curveCabinetLegs',
        label: <span className="text-title-medium">{t('Cabinet legs')}</span>,
        children: (
          <div className="flex flex-col gap-[12px] items-start self-stretch">
            <Form.Item
              layout="horizontal"
              className="self-stretch"
              label={t('Front leg cover panel')}
              name={'coTamChan'}
            >
              <Switch className="ml-auto" />
            </Form.Item>

            <Form.Item
              name={'caoChan'}
              layout="vertical"
              className="self-stretch"
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {t('Leg height')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitCaoChan?.min ?? 0) *
                      1000}{' '}
                    {t('to')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitCaoChan?.max ?? 0) *
                      1000}{' '}
                    mm)
                  </span>
                </span>
              }
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdOptions?.limitCaoChan;
                    return validatorSize({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                type="number"
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>

            <Form.Item
              layout="vertical"
              className="flex items-center gap-8 self-stretch"
              style={{ marginTop: '-10px' }}
            >
              <div className="flex items-center gap-8 self-stretch">
                <Divider
                  type="vertical"
                  style={{ margin: 0, height: '24px' }}
                />
                <ButtonSecondarySmall
                  posIcon={<Icon.ICSelectAll />}
                  title={t('Apply to all')}
                  type={'button'}
                  onClick={() => {
                    form
                      .validateFields(['caoChan'])
                      .then(values => {
                        if (getInputType(values.caoChan) !== 'number') return;
                        const caoChan = values.caoChan / 1000;

                        applyToAllCabinets(
                          {},
                          {
                            caoChan,
                          }
                        );

                        setTick(t => t + 1);
                      })
                      .catch(_ => {});
                  }}
                />
              </div>
            </Form.Item>
          </div>
        ),
      },
    ],
    hasBacksplash: [
      {
        key: 'hasBacksplash',
        label: <span className="text-title-medium">{t('Backsplash')}</span>,
        children: (
          <div className="flex flex-col gap-[12px] items-start self-stretch">
            <Form.Item
              layout="horizontal"
              className="self-stretch"
              label={t('With backsplash')}
              name={'hasBacksplash'}
            >
              <Switch className="ml-auto" />
            </Form.Item>
            {(mdSelectedModelData.mdOptions.opVachTrai ||
              mdSelectedModelData.mdOptions.opVachPhai) && (
              <>
                <Divider type="horizontal" className="m-0 !p-0" />
                <div className="flex flex-col items-start gap-[12px] self-stretch">
                  <Form.Item
                    layout="vertical"
                    className="self-stretch"
                    label={t('Position')}
                    name={'posBacksplash'}
                  >
                    <Radio.Group
                      block
                      options={[
                        ...[
                          { label: t('Left'), value: 'opVachTrai' },
                          { label: t('Right'), value: 'opVachPhai' },
                          { label: t('Both'), value: 'both' },
                        ].map(({ label, value }) => ({
                          label: (
                            <span className="text-label-medium font-semibold">
                              {label}
                            </span>
                          ),
                          value,
                          className:
                            '[&_.ant-radio-button-label]:!grow [&_.ant-radio-button-label]:!flex-shrink-0 !border-none !rounded-[1000px] py-[7px] px-[16px] flex justify-center items-center bg-[#F1F2F2]',
                        })),
                      ]}
                      className="flex items-center justify-start gap-[8px] text-black/60 "
                      optionType="button"
                      buttonStyle="solid"
                    />
                  </Form.Item>
                </div>
              </>
            )}
          </div>
        ),
      },
    ],
    edgeBacksplash: [
      {
        key: 'edgeBacksplash',
        label: (
          <span className="text-title-medium">{t('Edge backsplash')}</span>
        ),
        children: (
          <div className="flex flex-col gap-[12px] items-start self-stretch">
            <Form.Item
              layout="horizontal"
              className="self-stretch"
              label={t('With edge backsplash')}
              name={'edgeBacksplash'}
            >
              <Switch className="ml-auto" />
            </Form.Item>

            {(mdSelectedModelData.mdOptions.opVachBiaTrai ||
              mdSelectedModelData.mdOptions.opVachBiaPhai) && (
              <>
                <div className="flex flex-col items-start gap-[12px] self-stretch">
                  <Form.Item
                    layout="vertical"
                    className="self-stretch"
                    label={t('Position')}
                    name={'posEdgeBacksplash'}
                  >
                    <Radio.Group
                      block
                      options={[
                        ...[
                          { label: t('Left'), value: 'opVachBiaTrai' },
                          { label: t('Right'), value: 'opVachBiaPhai' },
                          { label: t('Both'), value: 'both' },
                        ].map(({ label, value }) => ({
                          label: (
                            <span className="text-label-medium font-semibold">
                              {label}
                            </span>
                          ),
                          value,
                          className:
                            '[&_.ant-radio-button-label]:!grow [&_.ant-radio-button-label]:!flex-shrink-0 !border-none !rounded-[1000px] py-[7px] px-[16px] flex justify-center items-center bg-[#F1F2F2]',
                        })),
                      ]}
                      className="flex items-center justify-start gap-[8px] text-black/60 "
                      optionType="button"
                      buttonStyle="solid"
                    />
                  </Form.Item>
                </div>
              </>
            )}

            <Divider type="horizontal" className="m-0 !p-0" />

            <Form.Item
              layout="horizontal"
              className="self-stretch"
              label={t('Curved edge backsplash')}
              name={'curveEdgeBacksplash'}
            >
              <Switch className="ml-auto" />
            </Form.Item>

            <Form.Item
              name={'rBoOpVachBia'}
              layout="vertical"
              className="self-stretch"
              label={
                <div className="flex flex-col justify-center items-start gap-[2px]">
                  <span className="body-text-module font-semibold">
                    {t('Curved radius')}
                    {mdSelectedModelData.mdOptions?.limitRBoOpVachBia && (
                      <span className="text-body-small">{`(R${(mdSelectedModelData.mdOptions?.limitRBoOpVachBia?.min ?? 0) * 1000} - R${(mdSelectedModelData.mdOptions?.limitRBoOpVachBia?.max ?? 0) * 1000})`}</span>
                    )}
                  </span>
                </div>
              }
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdOptions?.limitRBoOpVachBia;
                    if (max === undefined || min === undefined)
                      return Promise.resolve();
                    return validatorSize({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                disabled={!mdSelectedModelData.mdOptions?.curveEdgeBacksplash}
                addonAfter={'R(mm)'}
              />
            </Form.Item>
          </div>
        ),
      },
    ],
    countertopCurve: [
      {
        key: 'countertopCurve',
        label: (
          <span className="text-title-medium">
            {t('Kitchen island countertop')}
          </span>
        ),
        children: (
          <div className="flex flex-col gap-[12px] items-start self-stretch">
            <div className="flex flex-col gap-[8px] items-start self-stretch">
              <span
                className="body-text-module font-semibold"
                style={{ color: '#808B90' }}
              >
                {t('Mở rộng mặt bếp đảo (mm)')}
                {mdSelectedModelData.mdOptions?.limitExtend && (
                  <>
                    <br />
                    <span className="text-body-small">
                      {`(Từ ${(mdSelectedModelData.mdOptions?.limitExtend?.min ?? 0) * 1000} đến ${(mdSelectedModelData.mdOptions?.limitExtend?.max ?? 0) * 1000} mm)`}
                    </span>
                  </>
                )}
              </span>
              <div className="grid grid-cols-2 gap-[8px] self-stretch">
                <Form.Item
                  name={'extend2'}
                  layout="vertical"
                  className="self-stretch"
                  rules={[
                    {
                      validator: (_, value) => {
                        const { max, min } =
                          mdSelectedModelData.mdOptions?.limitExtend;

                        if (max === undefined || min === undefined)
                          return Promise.resolve();
                        return validatorSize({
                          value,
                          min,
                          max,
                        });
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonBefore={<Icon.ICExtend2 width={24} height={24} />}
                  />
                </Form.Item>

                <Form.Item
                  name={'extend3'}
                  layout="vertical"
                  className="self-stretch"
                  rules={[
                    {
                      validator: (_, value) => {
                        const { max, min } =
                          mdSelectedModelData.mdOptions?.limitExtend;

                        if (max === undefined || min === undefined)
                          return Promise.resolve();
                        return validatorSize({
                          value,
                          min,
                          max,
                        });
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonBefore={<Icon.ICExtend3 width={24} height={24} />}
                  />
                </Form.Item>
              </div>

              <div className="grid grid-cols-2 gap-[8px] self-stretch">
                <Form.Item
                  name={'extend4'}
                  layout="vertical"
                  className="self-stretch"
                  rules={[
                    {
                      validator: (_, value) => {
                        const { max, min } =
                          mdSelectedModelData.mdOptions?.limitExtend;

                        if (max === undefined || min === undefined)
                          return Promise.resolve();
                        return validatorSize({
                          value,
                          min,
                          max,
                        });
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonBefore={<Icon.ICExtend4 width={24} height={24} />}
                  />
                </Form.Item>

                <Form.Item
                  name={'extend1'}
                  layout="vertical"
                  className="self-stretch"
                  rules={[
                    {
                      validator: (_, value) => {
                        const { max, min } =
                          mdSelectedModelData.mdOptions?.limitExtend;

                        if (max === undefined || min === undefined)
                          return Promise.resolve();
                        return validatorSize({
                          value,
                          min,
                          max,
                        });
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonBefore={<Icon.ICExtend1 width={24} height={24} />}
                  />
                </Form.Item>
              </div>
            </div>

            <div className="flex flex-col gap-[8px] items-start self-stretch">
              <span
                className="body-text-module font-semibold"
                style={{ color: '#808B90' }}
              >
                {t('Góc bo cong (mm)')}
                {mdSelectedModelData.mdOptions?.limitRounded && (
                  <>
                    <br />
                    <span className="text-body-small">
                      {`(Từ ${(mdSelectedModelData.mdOptions?.limitRounded?.min ?? 0) * 1000} đến ${(mdSelectedModelData.mdOptions?.limitRounded?.max ?? 0) * 1000} mm)`}
                    </span>
                  </>
                )}
              </span>
              <div className="grid grid-cols-2 gap-[8px] self-stretch">
                <Form.Item
                  name={'rounded2'}
                  layout="vertical"
                  className="self-stretch"
                  rules={[
                    {
                      validator: (_, value) => {
                        const { max, min } =
                          mdSelectedModelData.mdOptions?.limitRounded;
                        if (max === undefined || min === undefined)
                          return Promise.resolve();
                        return validatorSize({
                          value,
                          min,
                          max,
                        });
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonBefore={<Icon.ICRounded2 width={24} height={24} />}
                  />
                </Form.Item>

                <Form.Item
                  name={'rounded3'}
                  layout="vertical"
                  className="self-stretch"
                  rules={[
                    {
                      validator: (_, value) => {
                        const { max, min } =
                          mdSelectedModelData.mdOptions?.limitRounded;

                        if (max === undefined || min === undefined)
                          return Promise.resolve();
                        return validatorSize({
                          value,
                          min,
                          max,
                        });
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonBefore={<Icon.ICRounded3 width={24} height={24} />}
                  />
                </Form.Item>
              </div>

              <div className="grid grid-cols-2 gap-[8px] self-stretch">
                <Form.Item
                  name={'rounded1'}
                  layout="vertical"
                  className="self-stretch"
                  rules={[
                    {
                      validator: (_, value) => {
                        const { max, min } =
                          mdSelectedModelData.mdOptions?.limitRounded;

                        if (max === undefined || min === undefined)
                          return Promise.resolve();
                        return validatorSize({
                          value,
                          min,
                          max,
                        });
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonBefore={<Icon.ICRounded1 width={24} height={24} />}
                  />
                </Form.Item>
                <Form.Item
                  name={'rounded4'}
                  layout="vertical"
                  className="self-stretch"
                  rules={[
                    {
                      validator: (_, value) => {
                        const { max, min } =
                          mdSelectedModelData.mdOptions?.limitRounded;

                        if (max === undefined || min === undefined)
                          return Promise.resolve();
                        return validatorSize({
                          value,
                          min,
                          max,
                        });
                      },
                    },
                  ]}
                >
                  <InputComponent
                    addonBefore={<Icon.ICRounded4 width={24} height={24} />}
                  />
                </Form.Item>
              </div>
            </div>
          </div>
        ),
      },
    ],
    bedDrawer: [
      {
        key: 'bedDrawer',
        label: <span className="text-title-medium">{t('Hộc kéo giường')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item
              layout="vertical"
              label={
                <span className="label-text-name flex gap-4 items-center grow">
                  {t('Quantity')}
                </span>
              }
              className="self-stretch grow"
              name={'qtyDrawer'}
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdOptions?.limitQtyDrawer;
                    return validatorQty({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={
                  <span className="text-body-medium"> {t('Piece')}</span>
                }
                placeholder={t('Enter')}
                size={'default'}
              />
            </Form.Item>

            <Form.Item
              layout="vertical"
              className="self-stretch"
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {t('Width')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitWDrawer?.min ?? 0) *
                      1000}{' '}
                    {t('to')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitWDrawer?.max ?? 0) *
                      1000}{' '}
                    mm)
                  </span>
                </span>
              }
              name={'wDrawer'}
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdOptions?.limitWDrawer;
                    return validatorSize({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>

            <Form.Item
              layout="vertical"
              label={
                <span className="label-text-name flex gap-4 items-center grow">
                  {t('Depth')}
                </span>
              }
              className="self-stretch grow"
              name={'dDrawer'}
            >
              <InputComponent
                disabled={true}
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
                size={'default'}
              />
            </Form.Item>

            <div className="flex items-end gap-8 self-stretch">
              <Form.Item
                layout="vertical"
                label={
                  <span className="label-text-name flex gap-4 items-center grow">
                    {t('Height')}
                  </span>
                }
                className="self-stretch grow"
                name={'hDrawer'}
              >
                <InputComponent
                  disabled={true}
                  addonAfter={<span className="text-body-medium">mm</span>}
                  placeholder={t('Enter')}
                  size={'default'}
                />
              </Form.Item>

              {['upper', 'ceiling'].some(item =>
                mdSelectedModelData.moduleType?.includes(item)
              ) &&
                !['shoe_upper_curve'].some(item =>
                  mdSelectedModelData.moduleType?.includes(item)
                ) && (
                  <Form.Item name={'fitCeiling'}>
                    <Tooltip title={t('Adjust ceiling height')}>
                      <ButtonIconOutlineMedium
                        type="button"
                        className={watchFitCeiling ? 'active' : ''}
                        icon={<Icon.ICHeight />}
                        onClick={() => {
                          handleFitCeiling(!watchFitCeiling);
                          form.setFieldsValue({
                            fitCeiling: !watchFitCeiling,
                          });
                          // setAllowFit(prev => {
                          //   return !prev;
                          // });
                        }}
                      />
                    </Tooltip>
                  </Form.Item>
                )}
            </div>
          </div>
        ),
      },
    ],
    bedFlipDoor: [
      {
        key: 'bedFlipDoor',
        label: (
          <span className="text-title-medium">{t('Cánh lật giường')}</span>
        ),
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item
              layout="vertical"
              label={
                <span className="label-text-name flex gap-4 items-center grow">
                  {t('Quantity')}
                </span>
              }
              className="self-stretch grow"
              name={'qtyFlipDoor'}
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdOptions?.limitQtyFlipDoor;
                    return validatorQty({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={
                  <span className="text-body-medium">{t('Piece')}</span>
                }
                placeholder={t('Enter')}
                size={'default'}
              />
            </Form.Item>

            <Form.Item
              layout="vertical"
              className="self-stretch"
              label={
                <span className="label-text-name flex gap-4 items-center">
                  {t('Width')}
                  <span className="support-text-medium">
                    ({t('From')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitWFlipDoor?.min ?? 0) *
                      1000}{' '}
                    {t('to')}{' '}
                    {(mdSelectedModelData.mdOptions?.limitWFlipDoor?.max ?? 0) *
                      1000}{' '}
                    mm)
                  </span>
                </span>
              }
              name={'wFlipDoor'}
              rules={[
                {
                  validator: (_, value) => {
                    const { max, min } =
                      mdSelectedModelData.mdOptions?.limitWFlipDoor;
                    return validatorSize({
                      value,
                      min,
                      max,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>

            {/* <Form.Item
              layout="vertical"
              label={
                <span className="label-text-name flex gap-4 items-center grow">
                  {t('Depth')}
                </span>
              }
              className="self-stretch grow"
              name={'dFlipDoor'}
            >
              <InputComponent
                disabled={true}
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
                size={'default'}
              />
            </Form.Item> */}
          </div>
        ),
      },
    ],
  };

  const formColumn = {
    column: [
      {
        key: 'column',
        label: <span className="text-title-medium">{t('Column')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item
              name={'dayCot'}
              layout="vertical"
              label={t('Column depth')}
              className="self-stretch"
              rules={[
                {
                  validator: (_, value) => {
                    const customMin = 0.05;
                    const customMax = mdSelectedModelData.mdDimensions.depth;

                    return validatorSize({
                      value,
                      min: customMin,
                      max: customMax,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
            <Form.Item
              name={'rongCot'}
              label={t('Column width')}
              layout="vertical"
              className="self-stretch"
              rules={[
                {
                  validator: (_, value) => {
                    const customMin = 0.05;
                    const customMax = Math.max(
                      1,
                      mdSelectedModelData.mdDimensions.width
                    );
                    // const customMax = mdSelectedModelData.mdDimensions.width;

                    return validatorSize({
                      value,
                      min: customMin,
                      max: customMax,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
            {mdSelectedModelData.mdOptions?.KCCot !== undefined ? (
              <Form.Item
                label={t('Column position (distance from the left edge)')}
                className="self-stretch"
                layout="vertical"
              >
                <div className="flex gap-8 items-center grow">
                  <Form.Item name={'KCCot'} className="grow">
                    <Slider
                      style={{ flex: '1 0 0' }}
                      value={mdSelectedModelData.mdOptions.KCCot * 1000}
                      max={
                        (mdSelectedModelData.mdDimensions.width -
                          mdSelectedModelData.mdOptions.rongCot) *
                        1000
                      }
                      step={1}
                      onChange={value => {
                        const KCCot = parseFloat(value) || 0;
                        handleScaleModule(null, {
                          KCCot: KCCot / 1000, // Convert mm to m
                        });
                      }}
                    />
                  </Form.Item>

                  <Form.Item name={'KCCot'}>
                    <InputComponent
                      addonAfter={<span className="text-body-medium">mm</span>}
                      placeholder={'--'}
                      size={'small'}
                      style={{ width: '100px' }}
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            ) : null}
          </div>
        ),
      },
    ],
  };

  const formBeam = {
    beam: [
      {
        key: 'beam',
        label: <span className="text-title-medium">{t('Beam')}</span>,
        children: (
          <div className="flex flex-col gap-16 items-start self-stretch">
            <Form.Item
              name={'rongDa'}
              layout="vertical"
              label={t('Beam width')}
              className="self-stretch"
              rules={[
                {
                  validator: (_, value) => {
                    const customMin = 0.05;
                    const customMax = mdSelectedModelData.mdDimensions.width;

                    return validatorSize({
                      value,
                      min: customMin,
                      max: customMax,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
            <Form.Item
              name={'caoDa'}
              label={t('Beam height')}
              layout="vertical"
              className="self-stretch"
              rules={[
                {
                  validator: (_, value) => {
                    const customMin = 0.01;
                    const customMax = mdSelectedModelData.mdDimensions.height;

                    return validatorSize({
                      value,
                      min: customMin,
                      max: customMax,
                    });
                  },
                },
              ]}
            >
              <InputComponent
                addonAfter={<span className="text-body-medium">mm</span>}
                placeholder={t('Enter')}
              />
            </Form.Item>
            {mdSelectedModelData.mdOptions?.KCCot !== undefined ? (
              <Form.Item
                label={t('Beam position (distance from the left edge)')}
                className="self-stretch"
                layout="vertical"
              >
                <div className="flex gap-8 items-center grow">
                  <Form.Item name={'KCCot'} className="grow">
                    <Slider
                      style={{ flex: '1 0 0' }}
                      value={mdSelectedModelData.mdOptions.KCCot * 1000}
                      max={
                        (mdSelectedModelData.mdDimensions.width -
                          mdSelectedModelData.mdOptions.rongDa) *
                        1000
                      }
                      step={1}
                      onChange={value => {
                        const KCCot = parseFloat(value) || 0;
                        handleScaleModule(null, {
                          KCCot: KCCot / 1000, // Convert mm to m
                        });
                      }}
                    />
                  </Form.Item>

                  <Form.Item name={'KCCot'}>
                    <InputComponent
                      addonAfter={<span className="text-body-medium">mm</span>}
                      placeholder={'--'}
                      size={'small'}
                      style={{ width: '100px' }}
                    />
                  </Form.Item>
                </div>
              </Form.Item>
            ) : null}
          </div>
        ),
      },
    ],
  };

  const handleFitCeiling = useCallback(
    allowFit => {
      if (allowFit)
        sceneManager.autoFitModuleController.fitCeilingCabinet(
          insideLoaderRef,
          selectedModel?.uuid
        );
      else
        sceneManager.autoFitModuleController.unFitCeilingCabinet(
          insideLoaderRef,
          selectedModel?.uuid
        );
      if (
        ['upper'].some(item => mdSelectedModelData.moduleType.includes(item))
      ) {
        setTimeout(() => {
          setTick(t => t + 1);
        }, 100);
      }

      const originalModule = sceneManager.cabinetManager.getModuleByGltfUuid(
        selectedModel?.uuid
      );
      if (originalModule) {
        const clonedScene = originalModule.group.clone();
        clonedScene.children = [
          clonedScene.children?.find(x => x.type === 'Group'),
        ];
        clonedScene.rotation.set(0, 0, 0);
        clonedScene.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(clonedScene);
        const size = new THREE.Vector3();
        box.getSize(size);
        setData({
          fitCeiling: allowFit,
          mdDimensions: { height: size.y },
          mdOptions: {},
        });
      }

      sceneManager.handleDrawBorderSelectedModule(selectedModel, true);
    },
    [
      insideLoaderRef,
      mdSelectedModelData,
      sceneManager,
      selectedModel,
      // allowFit,
    ]
  );

  const setData = useCallback(
    ({ fitCeiling, mdDimensions = {}, mdOptions = {}, module }) =>
      setMdSelectedModelData(prev => {
        const prevDims = prev.mdDimensions ?? {};
        const nextDims = { ...prevDims, ...mdDimensions };
        scaleJob.current = { module, prevDims, nextDims };
        return {
          ...prev,
          ...(fitCeiling !== undefined ? { fitCeiling: fitCeiling } : {}),
          mdDimensions: nextDims,
          mdOptions: { ...(prev.mdOptions ?? {}), ...mdOptions },
        };
      }),
    []
  );

  const applyToAllCabinets = (mdDimensions, mdOptions, allowFit) => {
    sceneManager.autoFitModuleController.applyToAllCabinets({
      insideLoaderRef,
      selectedModelId: selectedModel?.uuid,
      allowFit,
      mdDimensions,
      mdOptions,
    });

    setTimeout(async () => {
      await sceneManager?.calculateTotalCost?.({});
    }, 500);
    sceneManager?.handleDrawBorder();
  };

  // Debounced scale handler
  const debouncedValidate = useCallback(
    (changedValues, delay = 300) => {
      if (scaleTimerRef.current) {
        clearTimeout(scaleTimerRef.current);
      }

      const foundModule = currentCabinet.getModuleByGroupUuid(
        selectedModel.uuid
      );

      scaleTimerRef.current = setTimeout(async () => {
        const name = Object.keys(changedValues)[0];
        const value = changedValues[name];
        const typeInput = getInputType(value);
        if (typeInput === 'number') {
          try {
            await form.validateFields([name]); // chỉ validate field này
            if (['width', 'height', 'depth'].includes(name)) {
              const options = {};

              if (mdSelectedModelData.moduleType?.includes('bed_main')) {
                if (['depth'].includes(name)) {
                  if (
                    mdSelectedModelData.mdOptions.wDrawer >
                    value / 1000 / mdSelectedModelData.mdOptions.qtyDrawer
                  ) {
                    options.wDrawer = roundTo(
                      (value / 1000 - 0.1) /
                        mdSelectedModelData.mdOptions.qtyDrawer,
                      3
                    );
                  }

                  if (
                    mdSelectedModelData.mdOptions.wFlipDoor >
                    value / 1000 / mdSelectedModelData.mdOptions.qtyFlipDoor
                  ) {
                    options.wFlipDoor = roundTo(
                      (value / 1000 - 0.1) /
                        mdSelectedModelData.mdOptions.qtyFlipDoor,
                      3
                    );
                  }
                }
              }

              if (mdSelectedModelData.moduleType?.includes('bed_sub')) {
                if (['width'].includes(name)) {
                  if (
                    mdSelectedModelData.mdOptions.wFlipDoor >
                    value / 1000 / mdSelectedModelData.mdOptions.qtyFlipDoor
                  ) {
                    options.wFlipDoor = roundTo(
                      (value / 1000 - 0.1) /
                        mdSelectedModelData.mdOptions.qtyFlipDoor,
                      3
                    );
                  }
                }

                if (['depth'].includes(name)) {
                  if (
                    mdSelectedModelData.mdOptions.wDrawer >
                    value / 1000 / mdSelectedModelData.mdOptions.qtyDrawer
                  ) {
                    options.wDrawer = roundTo(
                      (value / 1000 - 0.1) /
                        mdSelectedModelData.mdOptions.qtyDrawer,
                      3
                    );
                  }

                  if (
                    mdSelectedModelData.mdOptions.dFlipDoor >
                    value / 1000 - 0.1
                  ) {
                    options.dFlipDoor = value / 1000 - 0.1;
                  }
                }
              }

              handleScaleModule({ [name]: value / 1000 }, options);
            } else if (
              [
                'caoChan',
                'KCCot',
                'dayCot',
                'rongCot',
                'dayDa',
                'rongDa',
                'caoDa',
                'rongK1',
                'rongK2',
                'caoVachLam',
                'caoKhungHoc',
                'rBo',
                'rBoOpVachBia',
                'DDTHau',

                'wDrawer',
                'hDrawer',
                'dDrawer',
                'wFlipDoor',
                'hFlipDoor',
                'dFlipDoor',
              ].includes(name)
            ) {
              handleScaleModule({}, { [name]: value / 1000 });

              // if (['rongCot'].includes(name)) {
              //   handleScaleModule(
              //     {},
              //     {
              //       KCCot: Math.min(
              //         value / 1000,
              //         mdSelectedModelData.mdDimensions.width - value / 1000
              //       ),
              //     }
              //   );
              // }

              if (['caoChan'].includes(name)) {
                insideLoaderRef.current.scaleInsideAfterScaleChanTu(
                  foundModule,
                  sceneRef.current
                );
              }
            } else if (
              name.startsWith('rounded') ||
              name.startsWith('extend')
            ) {
              handleScaleModule({}, { [name]: value / 1000 });
            } else if (['qtyDrawer', 'qtyFlipDoor'].includes(name)) {
              handleScaleModule({}, { [name]: value });
            }
          } catch (e) {}
        } else if (typeInput === 'boolean') {
          if (['curve'].includes(name)) {
            if (value) {
              handleScaleModule({}, { ['bct']: true });
              handleScaleModule({}, { ['bcp']: true });
            } else {
              handleScaleModule({}, { ['bct']: false });
              handleScaleModule({}, { ['bcp']: false });
            }

            await sceneManager.textureController.reloadTexture({
              module: foundModule,
            });
          }

          if (['edgeBacksplash'].includes(name)) {
            if (value) {
              handleScaleModule({}, { ['opVachBiaTrai']: true });
              handleScaleModule({}, { ['opVachBiaPhai']: true });
            } else {
              handleScaleModule({}, { ['opVachBiaTrai']: false });
              handleScaleModule({}, { ['opVachBiaPhai']: false });
            }

            // await sceneManager.textureController.reloadTexture({
            //   module: foundModule,
            // });
          }

          if (['curveEdgeBacksplash'].includes(name)) {
            if (value) {
              handleScaleModule({}, { ['opVachBiaBoCong']: true });
            } else {
              handleScaleModule({}, { ['opVachBiaBoCong']: false });
            }

            // await sceneManager.textureController.reloadTexture({
            //   module: foundModule,
            // });
          }

          if (['hasBacksplash'].includes(name)) {
            // const checkOpVachSau =
            //   foundModule.formularController.currentOptions.opVachSau !==
            //   undefined;
            const checkCornerModule =
              foundModule.moduleData.moduleType?.includes('corner');

            if (value) {
              if (checkCornerModule) {
                handleScaleModule({}, { ['opVachTrai']: true });
                handleScaleModule({}, { ['opVachPhai']: true });
              } else {
                handleScaleModule({}, { ['opVachSau']: true });
              }
            } else {
              if (checkCornerModule) {
                handleScaleModule({}, { ['opVachTrai']: false });
                handleScaleModule({}, { ['opVachPhai']: false });
              } else {
                handleScaleModule({}, { ['opVachSau']: false });
              }
            }

            // await sceneManager.textureController.reloadTexture({
            //   module: foundModule,
            // });
          }

          if (['coTamChan'].includes(name)) {
            if (value) {
              handleScaleModule({}, { ['coTamChan']: true });
            } else {
              handleScaleModule({}, { ['coTamChan']: false });
            }

            await sceneManager.textureController.reloadTexture({
              module: foundModule,
            });
          }

          if (['hauNgang'].includes(name)) {
            if (value) {
              handleScaleModule({}, { ['hauNgang']: true });
            } else {
              handleScaleModule({}, { ['hauNgang']: false });
            }

            await sceneManager.textureController.reloadTexture({
              module: foundModule,
            });
          }

          handleScaleModule({}, { [name]: value });
        } else if (typeInput === 'text') {
          if (['posCurve'].includes(name)) {
            handleScaleModule({}, { ['bct']: ['bct', 'both'].includes(value) });
            handleScaleModule({}, { ['bcp']: ['bcp', 'both'].includes(value) });
          }

          if (['posBacksplash'].includes(name)) {
            handleScaleModule(
              {},
              { ['opVachTrai']: ['opVachTrai', 'both'].includes(value) }
            );
            handleScaleModule(
              {},
              { ['opVachPhai']: ['opVachPhai', 'both'].includes(value) }
            );

            await sceneRef.current.updateAllCountertopAndBacksplash();
          }

          if (['posEdgeBacksplash'].includes(name)) {
            handleScaleModule(
              {},
              { ['opVachBiaTrai']: ['opVachBiaTrai', 'both'].includes(value) }
            );
            handleScaleModule(
              {},
              { ['opVachBiaPhai']: ['opVachBiaPhai', 'both'].includes(value) }
            );

            await sceneRef.current.updateAllCountertopAndBacksplash();
          }
        }
        setTimeout(async () => {
          await sceneManager?.calculateTotalCost?.({});
        }, 200);

        sceneRef.current.handleDrawBorderSelectedModule(selectedModel);
      }, delay);
    },
    [sceneManager, selectedModel, mdSelectedModelData]
  );

  const confirmScaleShoeDepth = () =>
    new Promise(resolve => {
      openNotification({
        key: 'warning-shoe-depth',
        icon: <Icon.ICWarning />,
        iconColor: '#F97D16',
        title: i18n.t('Module depth less than 330 mm'),
        description: i18n.t(
          'The module depth is below the required minimum; the fittings and internal cabinet partitions inside this module will be removed.'
        ),
        placement: 'bottomRight',
        classNames: {
          actions:
            'flex self-stretch grow items-center justify-end gap-[8px] p-[8px] border-t-[1px] border-[#E8E8E8]',
        },
        actions: (
          <div className="flex self-stretch grow items-center justify-end gap-[8px] p-[8px]">
            <ButtonSecondarySmall
              title={i18n.t('Cancel')}
              onClick={() => {
                closeNotification('warning-shoe-depth');
                resolve(false);
              }}
            />
            <ButtonPrimarySmall
              title={i18n.t('Delete all')}
              onClick={() => {
                closeNotification('warning-shoe-depth');
                resolve(true);
              }}
            />
          </div>
        ),
      });
    });

  const validateHauThickness = (group, hauThickness) => {
    const listHAU = [];
    group.traverse(child => {
      if (child.name.includes('HAU')) {
        listHAU.push(child);
      }
    });
    let flag = false;
    listHAU.forEach(hau => {
      const texturePrice = hau.userData?.texturePrice;
      if (texturePrice && !texturePrice[hauThickness]) {
        flag = true;
      }
    });
    if (flag) {
      closeNotification('texture-tam-hau-error');
      openNotification({
        icon: <Icon.ICWarning />,
        key: 'texture-tam-hau-error',
        title: t(
          'The board thickness cannot be changed because the current board type does not support it!'
        ),
        description: t(
          'Please select a new color code, this color code does not support a thickness of {{x}}mm.',
          { x: hauThickness * 1000 }
        ),
        duration: 2,
      });
      return false;
    }
    return true;
  };

  const handleScaleModule = async (mdDimensions, mdOptions) => {
    try {
      if (
        mdDimensions?.width === 0 ||
        mdDimensions?.height === 0 ||
        mdDimensions?.depth === 0
      ) {
        return;
      }

      //Xoá đường viền của module đang chọn trước khi scale
      sceneRef.current.removeBorderSelectedModule();

      const modules = sceneManager.cabinetManager.currentCabinet.modules;

      const modulesUpper = getUpperModuleList(modules);
      const modulesCeiling = getCeilingModuleList(modules);

      if (selectedModel?.userData?.type === ModuleType.BACKSPLASH) {
        await sceneManager.changeBacksplashHeight(mdDimensions.height);

        if (modulesUpper?.length || modulesCeiling?.length) {
          setTimeout(() => {
            openModalFitHeight({
              func: () => {
                sceneManager.autoFitModuleController.moveUpperFollowHeightestLowerCabinet(
                  { insideLoaderRef }
                );

                // sceneManager.autoFitModuleController.moveCeilingFollowHeightestUpperCabinet(
                //   { insideLoaderRef }
                // );

                setTimeout(() => {
                  setTick(t => t + 1);
                }, 0);
              },
            });
          });
        }

        return;
      }

      const module = sceneManager.cabinetManager.getModuleByGltfUuid(
        selectedModel?.uuid
      );

      if (
        (mdOptions?.DDTHau &&
          validateHauThickness(module?.group, mdOptions?.DDTHau)) ||
        mdOptions?.DDTHau == null
      ) {
        if (module?.cabinetType) {
          if (
            module.cabinetType.toLowerCase().includes('shoe') &&
            mdDimensions?.depth < 0.333
          ) {
            if (!module.cabinetType.toLowerCase().includes('bench')) {
              let check = false;
              module.listInside.forEach(inside => {
                if (inside.type === InsideType.HPANEL) {
                  check = true;
                }
              });
              if (check) {
                if (check) {
                  const confirmed = await confirmScaleShoeDepth();
                  if (!confirmed) return;
                }
              }
            }

            const accessoryFlip = module.listInside.find(
              inside => inside.type === InsideType.ACCESSORY_FLIP
            );
            if (accessoryFlip) {
              openNotification({
                key: 'cant-add-door-existing-door',
                icon: <Icon.ICWarning />,
                iconColor: '#F97D16',
                title: t("Can't change depth"),
                description: t(
                  'This module contains internal accessories with a minimum cabinet depth of 350.'
                ),
                placement: 'bottomRight',
                closable: true,
                duration: 4,
              });
              return;
            }
          }

          if (module.cabinetType.toLowerCase().includes('sink_lower')) {
            const sink = module.listInside.find(
              inside => inside.type === InsideType.ACCESSORY_SINK
            );
            if (sink) {
            }
          }

          //Cập nhật vị trí chân tủ TV sau khi scale
          let legData;

          if (module.cabinetType.toLowerCase().includes('tv')) {
            const legInside = module.listInside.find(
              inside =>
                inside.type === InsideType.CHAN_ROI_TV ||
                inside.type === InsideType.KHUNG_SAT
            );

            legData = {
              id: legInside?._id,
              model: legInside?.glb,
              type: legInside?.type,
              price: legInside?.price,
              name: legInside?.name,
              group_id: legInside?.groupId,
              code: legInside?.code,
            };
          }
          const originalModule =
            await sceneManager.autoFitModuleController.scaleModule(
              insideLoaderRef,
              selectedModel?.uuid,
              mdDimensions,
              mdOptions,
              module?.formularController?.currentDimensions?.height !==
                mdDimensions.height &&
                (modulesUpper.length > 0 || modulesCeiling.length > 0)
            );

          //Xoá chân cũ đi nếu có
          insideLoaderRef.current.removeLegModel(module);

          if (mdDimensions.height) {
            originalModule.fitCeiling = false;
          }

          const {
            currentDimensions: newMdDimensions,
            currentOptions: newMdOptions,
          } = originalModule.formularController;

          setData({
            fitCeiling: originalModule.fitCeiling,
            mdDimensions: newMdDimensions,
            mdOptions: newMdOptions,
            module: originalModule,
          });

          //Cập nhật vị trí chân tủ TV sau khi scale
          if (module.cabinetType.toLowerCase().includes('tv')) {
            let legHeight = 0;
            const addLegToCabinet = leg => {
              const box = new THREE.Box3().setFromObject(leg);
              const size = new THREE.Vector3();
              box.getSize(size);
              legHeight = size.y;

              const data = {
                _id: legData.id,
                glb: legData.model,
                type: legData.type,
                gltfUuid: leg.uuid,
                gltf: leg,
                thumbnail_url: leg.userData.thumbnail_url,
                price: legData.price,
                name: legData.name,
                groupId: legData.group_id,
                code: legData.code,
              };
              leg.userData.isInside = true;
              leg.userData.model = legData.model;

              module.listInside.push(data);
              module.group.add(leg);
            };

            await insideLoaderRef.current.loadLegModel(
              legData,
              module,
              addLegToCabinet
            );
          }

          if (
            module.cabinetType.toLowerCase().includes('kitchen') &&
            module.cabinetType.toLowerCase().includes('lower')
          ) {
            module.userData = { ...module.userData, option: newMdOptions };
          }
        }
      } else {
      }

      // setMdSelectedModelData(prevData => {
      //   const newData = {
      //     ...prevData,
      //     mdDimensions: newMdDimensions,
      //     mdOptions: newMdOptions,
      //     module: originalModule,
      //   };

      //   return newData;
      // });

      // setTimeout(() => {
      //   if (selectedModels.length) {
      //     sceneRef.current.removeBorderSelectedModule();

      //     selectedModels.forEach(group =>
      //       sceneRef.current.handleDrawBorderSelectedModule(group)
      //     );
      //   } else {
      //     sceneRef.current.removeBorderSelectedModule();
      //   }
      //   sceneRef.current.calculateTotalCost?.({});
      // }, 200);
    } catch (error) {
      console.error('Error scaling module:', error);
    }

    setTimeout(() => {
      if (selectedModels.length) {
        selectedModels.forEach(group =>
          sceneRef.current.handleDrawBorderSelectedModule(group, true)
        );
      }
    }, 200);

    setTimeout(() => {
      sceneRef.current.calculateTotalCost?.({});
    }, 200);
  };

  useEffect(() => {
    if (open) {
      setTab('1');
    }
  }, [open, currentModal]);

  useEffect(() => {
    if (selectedModel?.uuid !== currentModal) {
      setCurrentModal(selectedModel?.uuid);
    }
  }, [selectedModel]);

  return (
    <>
      {(mdSelectedModelData?.moduleType?.includes('column') ||
        mdSelectedModelData?.moduleType?.includes('beam')) && (
        <div className="right-cabinet-design-menu-header flex p-16 items-start gap-8 self-stretch flex-col bg-fills-white">
          <TabComponent
            items={[
              { key: '1', label: t('Frame') },
              ...(mdSelectedModelData?.moduleType?.includes('column')
                ? [{ key: '2', label: t('Column') }]
                : mdSelectedModelData?.moduleType?.includes('beam')
                  ? [{ key: '3', label: t('Beam') }]
                  : []),
            ]}
            style={{ width: 'fit-content', gap: '0' }}
            activeKey={tab}
            onChange={value => setTab(value)}
          />
        </div>
      )}

      <Form
        form={form}
        // layout="vertical"
        labelAlign="left"
        colon={false}
        onValuesChange={(changedValues, allValues) => {
          debouncedValidate(changedValues);
        }}
        validateTrigger={['onChange']}
        className="left-cabinet-design-menu-body flex flex-col items-start overflow-hidden p-8 gap-8 grow self-stretch"
      >
        <Collapse
          expandIcon={({ isActive }) => (
            <span className="w-16 h-16 flex">
              {isActive ? <Icon.ICExpandMore /> : <Icon.ICExpandLess />}
            </span>
          )}
          defaultActiveKey={[
            'cabinet',
            'cabinetLegs',
            'backPlank',
            'vachLam',
            'column',
            'beam',
            'drawer',
            'curve',
            'curveCabinetLegs',
            'backsplash',
            'hasBacksplash',
            'edgeBacksplash',
            'countertopCurve',
            'bedDrawer',
            'bedFlipDoor',
          ]}
          onChange={() => {}}
          className="self-stretch overflow-auto"
          items={[
            ...formLayout.map(item => ({
              ...item,
              onKeyDown: e => {
                e.stopPropagation();
              },
            })),
          ]}
        />
      </Form>
    </>
  );
};
